/**
 * Liveness Detection Utilities
 * ============================
 * Pure functions for head pose estimation and blink detection
 * using face landmarker landmarks (478 points).
 */

// Key landmark indices for head pose
const NOSE_TIP = 1;
const LEFT_EAR_TRAGION = 234;
const RIGHT_EAR_TRAGION = 454;
const FOREHEAD = 10;
const CHIN = 152;

// Eye landmark indices for EAR calculation
const LEFT_EYE = {
  top: [159, 158, 157],
  bottom: [145, 153, 154],
  inner: 133,
  outer: 33,
};
const RIGHT_EYE = {
  top: [386, 385, 384],
  bottom: [374, 380, 381],
  inner: 362,
  outer: 263,
};

/**
 * Calculate yaw (left/right head rotation) from face landmarks.
 * Negative = turned left, Positive = turned right.
 * @param {Array} landmarks - Face landmarker landmarks (478 points)
 * @returns {number} Yaw in degrees (-45 to +45 range)
 */
export function calculateYaw(landmarks) {
  const noseTip = landmarks[NOSE_TIP];
  const leftEar = landmarks[LEFT_EAR_TRAGION];
  const rightEar = landmarks[RIGHT_EAR_TRAGION];

  const faceWidth = Math.abs(rightEar.x - leftEar.x);
  if (faceWidth < 0.01) return 0; // Face too small

  const earMidX = (leftEar.x + rightEar.x) / 2;
  const noseCenterOffset = noseTip.x - earMidX;

  // Convert to approximate degrees
  return (noseCenterOffset / (faceWidth / 2)) * 45;
}

/**
 * Calculate pitch (up/down head rotation) from face landmarks.
 * Negative = looking down, Positive = looking up.
 * @param {Array} landmarks - Face landmarker landmarks
 * @returns {number} Pitch in degrees (-30 to +30 range)
 */
export function calculatePitch(landmarks) {
  const noseTip = landmarks[NOSE_TIP];
  const forehead = landmarks[FOREHEAD];
  const chin = landmarks[CHIN];

  const faceHeight = Math.abs(chin.y - forehead.y);
  if (faceHeight < 0.01) return 0;

  const expectedNoseY = (forehead.y + chin.y) / 2;
  const offset = noseTip.y - expectedNoseY;

  return (offset / (faceHeight / 2)) * 30;
}

/**
 * Calculate Eye Aspect Ratio (EAR) for one eye.
 * EAR drops significantly during a blink.
 * @param {Array} landmarks - Face landmarker landmarks
 * @param {Object} eyeIndices - Eye landmark indices
 * @returns {number} EAR value (typically 0.2-0.4 open, <0.2 blink)
 */
function calculateSingleEAR(landmarks, eyeIndices) {
  // Vertical distances (average of multiple points for stability)
  let verticalSum = 0;
  for (let i = 0; i < eyeIndices.top.length; i++) {
    const top = landmarks[eyeIndices.top[i]];
    const bottom = landmarks[eyeIndices.bottom[i]];
    verticalSum += Math.sqrt(
      Math.pow(top.x - bottom.x, 2) + Math.pow(top.y - bottom.y, 2)
    );
  }
  const avgVertical = verticalSum / eyeIndices.top.length;

  // Horizontal distance
  const inner = landmarks[eyeIndices.inner];
  const outer = landmarks[eyeIndices.outer];
  const horizontal = Math.sqrt(
    Math.pow(inner.x - outer.x, 2) + Math.pow(inner.y - outer.y, 2)
  );

  if (horizontal < 0.001) return 0.3; // Avoid division by zero

  return avgVertical / horizontal;
}

/**
 * Calculate average EAR for both eyes.
 * @param {Array} landmarks - Face landmarker landmarks
 * @returns {number} Average EAR value
 */
export function calculateEAR(landmarks) {
  const leftEAR = calculateSingleEAR(landmarks, LEFT_EYE);
  const rightEAR = calculateSingleEAR(landmarks, RIGHT_EYE);
  return (leftEAR + rightEAR) / 2;
}

/**
 * Detect a blink from EAR history.
 * A blink is: EAR drops below threshold, then recovers above it.
 * @param {Array} earHistory - Array of {ear, timestamp} entries
 * @param {number} threshold - EAR below this = eyes closed (default 0.2)
 * @param {number} maxDuration - Max blink duration in ms (default 500)
 * @returns {boolean} True if a blink was detected
 */
export function detectBlink(earHistory) {
  if (earHistory.length < 5) return false;

  const recent = earHistory.slice(-40);

  // Calculate adaptive baseline from the highest EAR values (eyes open)
  const sortedEars = recent.map(f => f.ear).sort((a, b) => b - a);
  const topHalf = sortedEars.slice(0, Math.max(3, Math.floor(sortedEars.length / 2)));
  const baseline = topHalf.reduce((a, b) => a + b, 0) / topHalf.length;

  // Adaptive threshold: 60% of the user's open-eye baseline
  const threshold = baseline * 0.6;

  // Method 1: open -> closed -> open pattern
  let closedStart = -1;
  for (let i = 0; i < recent.length; i++) {
    if (recent[i].ear < threshold) {
      if (closedStart === -1) closedStart = i;
    } else if (closedStart !== -1) {
      return true; // Eyes closed then reopened = blink
    }
  }

  // Method 2: eyes currently closed (last 2+ frames significantly below baseline)
  const lastFrames = recent.slice(-3);
  if (lastFrames.length >= 2) {
    const closedFrames = lastFrames.filter(f => f.ear < threshold).length;
    const hasOpenBefore = recent.slice(0, -3).some(f => f.ear >= baseline * 0.8);
    if (closedFrames >= 2 && hasOpenBefore) {
      return true;
    }
  }

  return false;
}

/**
 * Check if face is properly centered and sized in the frame.
 * @param {Array} landmarks - Face landmarker landmarks
 * @returns {Object} { centered, tooClose, tooFar, faceSize }
 */
export function checkFacePosition(landmarks) {
  const leftEar = landmarks[LEFT_EAR_TRAGION];
  const rightEar = landmarks[RIGHT_EAR_TRAGION];
  const forehead = landmarks[FOREHEAD];
  const chin = landmarks[CHIN];

  // Face center
  const centerX = (leftEar.x + rightEar.x) / 2;
  const centerY = (forehead.y + chin.y) / 2;

  // Face size (as fraction of frame)
  const faceWidth = Math.abs(rightEar.x - leftEar.x);
  const faceHeight = Math.abs(chin.y - forehead.y);
  const faceSize = Math.max(faceWidth, faceHeight);

  // Check centering (within 22% of center — relaxed for mobile front cameras)
  const centeredX = Math.abs(centerX - 0.5) < 0.22;
  const centeredY = Math.abs(centerY - 0.5) < 0.22;
  const centered = centeredX && centeredY;

  // Check size
  const tooClose = faceSize > 0.7;
  const tooFar = faceSize < 0.2;

  return { centered, tooClose, tooFar, faceSize };
}

/**
 * Validate that a challenge condition is met.
 * @param {string} challengeId - Challenge identifier
 * @param {Array} landmarks - Current face landmarks
 * @param {Array} earHistory - EAR history for blink detection
 * @returns {boolean} True if challenge condition is satisfied
 */
export function validateChallenge(challengeId, landmarks, earHistory) {
  const yaw = calculateYaw(landmarks);
  const pitch = calculatePitch(landmarks);
  const position = checkFacePosition(landmarks);

  switch (challengeId) {
    case 'center':
      return position.centered && !position.tooClose && !position.tooFar &&
        Math.abs(yaw) < 10 && Math.abs(pitch) < 10;

    case 'turn_left':
      return yaw > 12; // Positive yaw = user turns left (mirrored video)

    case 'turn_right':
      return yaw < -12; // Negative yaw = user turns right (mirrored video)

    case 'blink':
      return detectBlink(earHistory);

    default:
      return false;
  }
}
